import { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { UnifiedChatRouter, UnifiedChatResponse } from '@/services/UnifiedChatRouter';
import { detectDemoIntent } from '@/services/DemoIntentService';
import { apiClient } from '@/lib/api-client';
import { AI_ORCHESTRATOR_PATH } from '@/lib/api-routes';
import { processExcel, ExcelAnalysisResult } from '@/services/ExcelProcessor';

function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export const useUnifiedConversation = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId, setSessionId] = useState(generateSessionId);
  const location = useLocation();

  // Extract current page from URL path
  const currentPage = location.pathname.split('/')[1] || 'dashboard';

  const processQuery = async (query: string): Promise<UnifiedChatResponse> => {
    setIsProcessing(true);

    try {
      // Check for demo mode
      const isDemoMode = sessionStorage.getItem('demo_mode') === 'true';

      // IMPORTANT: In demo mode, skip edge function and use local processing with seeded data
      if (isDemoMode) {
        console.log('[useUnifiedConversation] Demo mode active - using local processing with seeded data');
        return await processQueryLocally(query);
      }

      // BUG-103 / BUG-87: the Phase-1 financial intents are answered LOCALLY,
      // before the orchestrator is consulted at all.
      //
      // Cash position, receivables, payables and overdue are the only answers
      // whose figures are derived from the signed-in tenant's own records via
      // `fetchDemoFinancialData()` — the same source the dashboard cards read —
      // so they are the only ones that reconcile to the dashboard to the dollar.
      // They reached that path by ACCIDENT until now: the orchestrator call went
      // to a route the API never mounted, `apiClient` threw on the 404, and the
      // catch below fell through to local processing. Correcting the path (AC4)
      // removes the accident. Without this gate a reachable orchestrator answers
      // first, and BUG-87's deterministic figures get silently replaced by
      // whatever the model says the moment a provider key is configured.
      //
      // This is deliberately the SAME condition `UnifiedChatRouter` applies at
      // its own entry (`processUnifiedQuery`), on the same bilingual detector,
      // so nothing the deterministic path handles can be lost on the way in.
      // The orchestrator now only ever sees the queries that path cannot answer.
      const seededIntent = detectDemoIntent(query);
      if (seededIntent !== 'out_of_scope') {
        console.log('[useUnifiedConversation] Phase-1 intent resolved locally:', seededIntent);
        return await processQueryLocally(query);
      }

      // Build context
      const context = {
        currentPage,
        persona: localStorage.getItem('persona') || 'controller',
        manufacturingType: localStorage.getItem('manufacturingType') || 'general',
        businessOutcome: localStorage.getItem('businessOutcome') || 'optimization',
        priorities: getStoredPriorities(),
        userId: getCurrentUserId(),
        companyId: getCurrentCompanyId()
      };

      // Try to use the new AI orchestrator API route
      try {
        // BUG-87: the orchestrator calls a frontier model server-side, so it
        // gets a longer budget than the default — but it still HAS one. It used
        // to be unbounded, which meant a stalled provider hung the chat forever.
        // BUG-103 AC4: the path is imported, never spelled out here. Two
        // literals drifted apart once already (`/ai/orchestrator` here vs
        // `/ai/orchestrate` mounted) and 404'd every message for months.
        const data = await apiClient.post<UnifiedChatResponse>(AI_ORCHESTRATOR_PATH, {
          query,
          context,
          sessionId,
          timestamp: new Date().toISOString(),
          isDemoMode: false
        }, { timeoutMs: 60_000 });

        if (data) {
          return data;
        }
      } catch (apiError) {
        console.warn('AI orchestrator API error, falling back to local processing:', apiError);
      }

      // Fallback to local processing
      console.warn('No data from AI orchestrator, using local unified processing');
      return await processQueryLocally(query);
    } catch (error) {
      console.error('Unified conversation processing error:', error);
      // Fallback to local processing
      return await processQueryLocally(query);
    } finally {
      setIsProcessing(false);
    }
  };

  const processQueryLocally = async (query: string): Promise<UnifiedChatResponse> => {
    console.log('[useUnifiedConversation] processQueryLocally called for:', query);
    console.log('[useUnifiedConversation] Demo mode check:', sessionStorage.getItem('demo_mode'));
    try {
      const router = new UnifiedChatRouter();

      return await router.processUnifiedQuery(query, {
        currentPage,
        persona: localStorage.getItem('persona') || 'controller',
        manufacturingType: localStorage.getItem('manufacturingType') || 'general',
        businessOutcome: localStorage.getItem('businessOutcome') || 'optimization',
        priorities: getStoredPriorities(),
        sessionId,
        timestamp: new Date()
      });
    } catch (error) {
      console.error('Local processing error:', error);
      // Return a simple fallback response
      return {
        conversationalText: `I understand you're asking about "${query}". I'm working on processing this request. Try asking about cash position, payments, emails, or general help.`,
        primaryAgent: 'System',
        supportingAgents: [],
        suggestedActions: [
          { actionType: 'help', label: 'Get Help', priority: 1 },
          { actionType: 'retry', label: 'Try Again', priority: 2 }
        ],
        confidence: 0.5,
        agentResponses: {}
      };
    }
  };

  const processQueryWithFile = async (
    query: string,
    file?: File
  ): Promise<UnifiedChatResponse & { excelAnalysis?: ExcelAnalysisResult }> => {
    setIsProcessing(true);

    try {
      let enrichedQuery = query;
      let excelAnalysis: ExcelAnalysisResult | undefined;

      // If file is provided and is Excel, process it first
      if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
        try {
          const context = {
            currentPage,
            persona: localStorage.getItem('persona') || 'controller',
            manufacturingType: localStorage.getItem('manufacturingType') || 'general',
            businessOutcome: localStorage.getItem('businessOutcome') || 'optimization',
            priorities: getStoredPriorities(),
            userId: getCurrentUserId(),
            companyId: getCurrentCompanyId()
          };

          excelAnalysis = await processExcel(file, query, context);
          enrichedQuery = excelAnalysis.enrichedQuery;

          console.log('Excel analysis:', {
            type: excelAnalysis.documentType,
            agent: excelAnalysis.selectedAgent,
            metrics: excelAnalysis.metrics
          });
        } catch (error) {
          console.error('Excel processing failed:', error);
        }
      }

      // Process the query (enriched with Excel data if available)
      const response = await processQuery(enrichedQuery);

      return {
        ...response,
        excelAnalysis
      };
    } finally {
      setIsProcessing(false);
    }
  };

  /** Reset the session ID to start a fresh conversation. */
  const resetSession = useCallback(() => {
    setSessionId(generateSessionId());
  }, []);

  return { processQuery, processQueryWithFile, isProcessing, sessionId, resetSession };
};

// Utility functions
const getAuthToken = (): string => {
  return localStorage.getItem('auth_token') || '';
};

const getCurrentUserId = (): string => {
  return localStorage.getItem('user_id') || 'default_user';
};

const getCurrentCompanyId = (): string => {
  return localStorage.getItem('company_id') || 'default_company';
};

const getStoredPriorities = () => {
  try {
    return JSON.parse(localStorage.getItem('user_priorities') || '{}');
  } catch {
    return {
      cash_flow: 'high',
      payment_acceleration: 'medium',
      vendor_management: 'medium',
      contract_qualification: 'low',
      cross_border: 'low',
      risk_monitoring: 'medium'
    };
  }
};
